import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { any, func, string } from 'prop-types';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  HANDOFF_DECISION,
  HANDOFF_DESTINATION,
  HANDOFF_REASON,
  destinationUrl,
  isPlainLeftClick,
  requestCheckoutHandoff,
} from '../../util/cfHandoff';
import { idleCreditCount } from '../../util/onboardingCohort';
import { recordAskOutcome } from '../../util/askOutcome';
import IconSpinner from '../IconSpinner/IconSpinner';
import CheckoutHandoffModal from '../CheckoutHandoffModal/CheckoutHandoffModal';

import css from './CheckoutHandoffLink.module.css';

// ---------------------------------------------------------------------------
// Story 3.16 AC1a — THE GATED ANCHOR, shared by Door 3 (ListYourHorseLink, 9
// call sites) and Door 5 (ManageListingsPage "See how it works").
//
// ONE implementation on purpose: two hand-rolled copies of "preventDefault →
// gate → navigate or ask" are exactly how the CF URL itself ended up defined
// three times. If a third door ever needs the gate, it uses this.
//
// 🔴 IT RENDERS AN `<a href>` AND MUST KEEP DOING SO (AC1a.1 / AC1a.3).
// Not a <button>. The href works before hydration and with JS disabled — the
// gate is an ENHANCEMENT over a working link, never a replacement for it — and
// converting the element would change keyboard semantics and screen-reader
// announcement across nine call sites. React event handlers are not DOM
// attributes, so attaching onClick leaves the rendered markup byte-identical.
//
// ⚠️ LOGGED-OUT RENDERS ATTACH NO GATE (AC1a.4 / AC6.2 / RP-8): no
// preventDefault, no fetch. A caller's onClick / onHandled still runs (ticket 73).
// Not attached-then-short-circuited: a visitor with no resolved currentUser
// cannot hold a paid listing and has no identity to check, and this is the
// paid-ad hot path. A gate that cannot change the answer has no business
// running on it.
// ---------------------------------------------------------------------------

/**
 * @param {Object} props
 * @param {import('react').ReactNode} props.children
 * @param {string} [props.className]
 * @param {string} [props.destination] one of HANDOFF_DESTINATION
 * @param {Function} [props.onHandled] called once this link has finished
 *   handling a click: straight away for a click the gate does not take, after
 *   the gate has answered for one it does, never when the answer is dropped.
 *   It must not unmount this link: the ask modal is owned by it. If it throws,
 *   the answer is still acted on.
 */
const CheckoutHandoffLink = ({
  children,
  className,
  destination = HANDOFF_DESTINATION.SELLER_LANDING,
  // Mockable external-navigation seam, mirroring EditListingPage's
  // `navigateExternal`: "NOT a bare inline window.location assignment so the
  // seam can be mocked". jsdom refuses to let window.location be replaced, so
  // without this the navigation legs are untestable.
  navigateExternal = url => window.location.assign(url),
  onHandled,
  ...rest
}) => {
  const currentUser = useSelector(state => state.user.currentUser);
  const hasAnyOwnListing = useSelector(state => state.user.hasAnyOwnListing);
  const history = useHistory();

  const [pending, setPending] = useState(false);
  const [handoff, setHandoff] = useState(null);
  // A second click while the first round-trip is still in flight must not fire
  // another request, and must not race two navigations.
  const inFlightRef = useRef(false);

  // Ticket 73 (a) — an answer that arrives after the seller has left the page
  // is dropped. Set inside the effect, not only at init, so a StrictMode
  // remount in development does not leave it false.
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const href = destinationUrl(destination);
  const { onClick: callerOnClick, ...anchorProps } = rest;

  const onAnchorClick = useCallback(
    async event => {
      // Any caller-supplied handler runs first. If it took the click, or this
      // is a modifier / middle click, we do NOT intercept and today's behaviour
      // stands: the browser follows the bare href (AC1a.2 / RP-7).
      if (typeof callerOnClick === 'function') {
        callerOnClick(event);
      }
      if (!isPlainLeftClick(event)) {
        if (typeof onHandled === 'function') {
          onHandled();
        }
        return;
      }

      // 🔴 COLD-REVIEW B-1(b) — preventDefault BEFORE the in-flight check, NOT
      // after. This ordering is the whole fix and it is load-bearing.
      //
      // The in-flight guard was written to stop a second FETCH, and it did.
      // But by returning early it also skipped preventDefault, so the second
      // click of a double-click kept the anchor's native navigation and the
      // browser went straight to the ClickFunnels funnel — the gate bypassed
      // entirely, no ask screen, no telemetry row, no Slack alert. That is the
      // exact double-charge this story exists to prevent, reachable by the most
      // ordinary impatient gesture there is.
      //
      // ⚠️ The old test could not see this: it asserted
      // `fetchAvailableTiers` was called once, which stays green because jsdom
      // silently no-ops anchor navigation. The assertion that catches it is
      // `defaultPrevented` on BOTH clicks, and that is what now ships.
      event.preventDefault();
      if (inFlightRef.current) {
        return;
      }

      inFlightRef.current = true;
      setPending(true);

      // Ticket 73 (a) — "left the page" is ANY pathname change while the gate
      // is pending, so A → B → A still counts. A query-only change (search
      // filters) is the same page. No router means no evidence of leaving, so
      // nothing is dropped on that ground.
      const clickedOn = history?.location?.pathname;
      let leftPage = false;
      const stopListening = history?.listen
        ? history.listen(location => {
            if (location.pathname !== clickedOn) {
              leftPage = true;
            }
          })
        : null;

      let result;
      try {
        result = await requestCheckoutHandoff({ currentUser, hasAnyOwnListing, destination });
      } catch (e) {
        // 🔴 COLD-REVIEW SF-5 — the default navigation has ALREADY been
        // cancelled by this point, so a throw here used to produce nothing at
        // all: no navigation, no modal, no error — a permanently dead CTA plus
        // an unhandled rejection. The gate's declared posture is "fail toward
        // ask, never toward silent checkout"; the undeclared third outcome was
        // "fail toward nothing". Fail toward ASK instead, consistent with
        // AC1.3, using whatever the gate managed to resolve.
        //
        // 🔴 RE-REVIEW FR-5 — THE TWO VALUES BELOW USED TO BE WRONG, NOT NEUTRAL.
        //
        // This fallback hard-coded `idleSlots: 0`, which rendered the very
        // falsehood FIX-3 had just removed one file away: "You've already paid
        // for 0 listings." And it hard-coded `reason:
        // INCONCLUSIVE_MIRROR_POSITIVE` for ANY throw, so an unknown internal
        // exception was persisted as a specific, meaningful branch observation
        // — corrupting the exact counts these rows exist to produce.
        //
        // Both are now honest: the count comes from the mirror (the same source
        // the gate's own inconclusive leg uses, and the only evidence available
        // when the gate has failed), and the reason says what actually
        // happened.
        //
        // ---------------------------------------------------------------------
        // 🔴🔴 RE-REVIEW ROUND 3, RR-2 — AND A ZERO MIRROR IS NOT AN ASK.
        // ---------------------------------------------------------------------
        // The previous version of this comment ended: *"A caller that cannot
        // read the mirror shows no count at all rather than a made-up one."*
        // THAT WAS FALSE. `safeMirror` is `0` in exactly the cannot-read case,
        // and `0` is not an absence — it is a sentence about money. Driven
        // through the real component, a mirror of 0 / absent / non-numeric
        // rendered:
        //
        //     "You've already paid for 0 listings"
        //     "You've bought 0 listings that haven't been posted yet."
        //
        // …plus a wizard route with NOTHING SPENDABLE. That is the same
        // falsehood-about-money class FIX-3 existed to kill, and it violated
        // two invariants written into AC2.6 in the very round that shipped it:
        // "the modal never asserts a number it cannot source", and
        // "`spendableSlots` is the ONLY thing that may offer the wizard route".
        //
        // ⚠️ THE FIX IS TO STOP HAVING A SECOND POSTURE. One file away, on
        // identical evidence, the gate PROCEEDS: `cfHandoff.js` — "a seller
        // whose mirror also reads zero has no evidence of a slot from any
        // source". This fallback asked. Two answers to one question, and the
        // gate's is the one with a test pinning it ("a negative mirror on the
        // error leg proceeds rather than asking on phantom credits").
        //
        // So: mirror > 0 is evidence, and we ask on it exactly as the gate's
        // inconclusive leg does. Mirror at or below zero is NO evidence from
        // ANY source, and the honest answer is the one the gate already gives.
        // The `gate_threw` reason is carried on BOTH branches, so the AC5 rows
        // still say an internal exception happened either way — that was FR-5's
        // real contribution and it is untouched.
        const mirror = idleCreditCount(currentUser);
        const safeMirror = Number.isFinite(mirror) ? Math.max(0, Math.floor(mirror)) : 0;
        result = {
          decision: safeMirror > 0 ? HANDOFF_DECISION.ASK : HANDOFF_DECISION.PROCEED,
          reason: HANDOFF_REASON.GATE_THREW,
          idleSlots: safeMirror,
          tierBreakdown: [],
          paidNotSpendable: [],
          url: href,
          destination,
          error: (e && e.message) || 'CHECKOUT_HANDOFF_THREW',
        };
      } finally {
        if (stopListening) {
          stopListening();
        }
        setPending(false);
        inFlightRef.current = false;
      }

      // Card decision 1: quietly dropped — no navigation, no ask screen, no
      // telemetry row, and the caller is not told the click was answered.
      if (leftPage || !mountedRef.current) {
        return;
      }
      if (typeof onHandled === 'function') {
        try {
          onHandled();
        } catch (e) {
          // T73R-F2: a caller's callback must never cancel the seller's answer —
          // the native navigation was prevented long ago.
        }
      }

      if (result.decision === HANDOFF_DECISION.PROCEED) {
        // AC5.6.3 / RP-4c — the [[#592]] inverse disagreement is RECORDED here
        // and changes nothing about the decision. Fire-and-forget (AC5.5).
        recordAskOutcome({ outcome: 'proceeded', handoff: result });
        navigateExternal(result.url);
        return;
      }
      // T73R-F3: the modal lives in this link's state. If onHandled unmounted the
      // link, nothing can be shown, so nothing is recorded as shown.
      if (!mountedRef.current) {
        return;
      }
      recordAskOutcome({ outcome: 'shown', handoff: result });
      setHandoff(result);
    },
    [
      callerOnClick,
      currentUser,
      hasAnyOwnListing,
      destination,
      navigateExternal,
      href,
      history,
      onHandled,
    ]
  );

  // No identity → no gate, no handler, no fetch. Byte-identical to pre-3.16.
  if (!currentUser?.id) {
    // Ticket 73 decision 10 — a logged-out click is never gated, so it is
    // handled the moment it happens. Composed after the caller's own onClick,
    // and only when one is supplied: no preventDefault, no fetch (RP-8).
    const loggedOutOnClick =
      typeof onHandled === 'function'
        ? event => {
            if (typeof callerOnClick === 'function') {
              callerOnClick(event);
            }
            onHandled();
          }
        : callerOnClick;
    return (
      <a href={href} className={className} {...anchorProps} onClick={loggedOutOnClick}>
        {children}
      </a>
    );
  }

  return (
    <>
      {/* SF-8 — the pending state is now VISIBLE (dim + progress cursor +
          spinner), not `aria-busy` alone. See CheckoutHandoffLink.module.css
          for why that mattered enough to be a named finding. */}
      <a
        href={href}
        className={classNames(className, { [css.pending]: pending })}
        onClick={onAnchorClick}
        aria-busy={pending || undefined}
        {...anchorProps}
      >
        {children}
        {/* FR-N4 — decorative. `aria-busy` on the anchor already announces the
            state, so an unnamed role="img" here would be a second, empty
            announcement (an axe violation). Hidden rather than labelled. */}
        {pending ? (
          <span aria-hidden="true">
            <IconSpinner className={css.spinner} />
          </span>
        ) : null}
      </a>
      <CheckoutHandoffModal
        handoff={handoff}
        onClose={() => setHandoff(null)}
        onOutcome={recordAskOutcome}
        navigateExternal={navigateExternal}
      />
    </>
  );
};

CheckoutHandoffLink.propTypes = {
  children: any,
  className: string,
  destination: string,
  navigateExternal: func,
  onHandled: func,
};

export default CheckoutHandoffLink;
